/**
 * SpaceHawk shared interactions — small, dependency-free, event-delegated.
 * Loaded ONCE with `defer` from spacehawk-header.liquid. Three pieces:
 *   1. Video facades — click opens a centered modal/lightbox with a YouTube
 *      embed that autoplays WITH SOUND, on both mobile and desktop.
 *   2. Hero image gallery — swipeable main-image carousel (scroll-snap,
 *      [data-sh-maintrack]/[data-sh-slide]) synced with the thumbnail
 *      strip in both directions: thumbnail click scrolls the carousel,
 *      and swiping the carousel updates the active thumbnail.
 *   3. Add-to-cart — AJAX submit + header cart-count badge + toast, self-
 *      contained (no Dawn cart-drawer dependency — see spacehawk-header.liquid
 *      for why). Reads Shopify's cart routes from window.SHConfig, which the
 *      header section prints inline (this file can't use Liquid tags itself).
 * All three use event delegation on document, so they work for content added
 * dynamically (e.g. after a theme-editor re-render) with no re-init needed.
 */

// ── 1. Video facades — popup player, unmuted ─────────────────────────────
// Clicking a facade opens a centered modal (backdrop + video box + close
// button) appended to <body>, instead of swapping the iframe in place. The
// embed URL has NO mute=1 — the popup, opened directly inside a real click
// handler, is the strongest "user gesture" context a page can offer, so this
// is the best achievable version of unmuted-on-first-tap for both mobile and
// desktop. It still isn't a 100%-guaranteed browser contract: an unusually
// strict mobile configuration may occasionally hold playback muted/paused
// behind YouTube's own in-player play button as a one-tap fallback — that's
// normal, universal third-party-iframe autoplay behavior everywhere on the
// web, not a bug in this file, and nothing on our side can force a
// cross-origin document's audio open. playsinline=1 keeps iOS Safari from
// hijacking playback into the OS's native fullscreen video player, so it
// stays inside this modal's own chrome. This mechanism is shared by every
// facade (SpaceHawk Reviews' video wall, SpaceHawk Video Feature), so both
// sections get the popup automatically.
//
// Closing the modal (× button, Escape key, or a click on the dark backdrop)
// REMOVES the iframe element from the DOM entirely — not just display:none —
// because a hidden-but-still-attached YouTube iframe keeps playing audio
// invisibly in the background otherwise.
var SH_VMODAL_STYLE_ID = 'sh-vmodal-style';

function shInjectVideoModalStyles() {
  if (document.getElementById(SH_VMODAL_STYLE_ID)) return;
  var style = document.createElement('style');
  style.id = SH_VMODAL_STYLE_ID;
  style.textContent =
    '.sh-vmodal{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(10,14,20,.86);}' +
    '.sh-vmodal__inner{position:relative;width:100%;max-width:960px;}' +
    '.sh-vmodal__frame-wrap{position:relative;width:100%;aspect-ratio:16/9;background:#000;border-radius:12px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.5);}' +
    '.sh-vmodal__frame-wrap iframe{position:absolute;inset:0;width:100%;height:100%;border:0;display:block;}' +
    '.sh-vmodal__close{position:absolute;top:-44px;right:0;z-index:2;width:36px;height:36px;border-radius:50%;border:0;background:rgba(255,255,255,.14);color:#fff;font-size:20px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;}' +
    '.sh-vmodal__close:hover{background:rgba(255,255,255,.26);}' +
    'body.sh-vmodal-open{overflow:hidden;}' +
    '@media (max-width:640px){.sh-vmodal{padding:0;}.sh-vmodal__inner{max-width:100%;}.sh-vmodal__frame-wrap{border-radius:0;}.sh-vmodal__close{top:12px;right:12px;background:rgba(0,0,0,.5);}}';
  document.head.appendChild(style);
}

var shVideoModalState = { el: null, lastTrigger: null, onKeydown: null };

function shCloseVideoModal() {
  var state = shVideoModalState;
  if (!state.el) return;
  state.el.remove(); // removes the iframe with it — stops playback
  state.el = null;
  document.body.classList.remove('sh-vmodal-open');
  if (state.onKeydown) document.removeEventListener('keydown', state.onKeydown, false);
  state.onKeydown = null;
  if (state.lastTrigger && typeof state.lastTrigger.focus === 'function') {
    state.lastTrigger.focus();
  }
  state.lastTrigger = null;
}

function shOpenVideoModal(videoId, label, trigger) {
  shInjectVideoModalStyles();
  shCloseVideoModal(); // only one modal open at a time

  var backdrop = document.createElement('div');
  backdrop.className = 'sh-vmodal';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', label || 'Video');

  var inner = document.createElement('div');
  inner.className = 'sh-vmodal__inner';

  var closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'sh-vmodal__close';
  closeBtn.setAttribute('aria-label', 'Close video');
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', shCloseVideoModal, false);

  var frameWrap = document.createElement('div');
  frameWrap.className = 'sh-vmodal__frame-wrap';

  var iframe = document.createElement('iframe');
  iframe.setAttribute('src', 'https://www.youtube.com/embed/' + encodeURIComponent(videoId) + '?autoplay=1&rel=0&playsinline=1');
  iframe.setAttribute('title', label || 'Video');
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
  iframe.setAttribute('allowfullscreen', '');

  frameWrap.appendChild(iframe);
  inner.appendChild(closeBtn);
  inner.appendChild(frameWrap);
  backdrop.appendChild(inner);

  // Click on the dark backdrop itself (not inside the video box) closes it.
  backdrop.addEventListener('click', function (event) {
    if (event.target === backdrop) shCloseVideoModal();
  }, false);

  document.body.appendChild(backdrop);
  document.body.classList.add('sh-vmodal-open');

  shVideoModalState.el = backdrop;
  shVideoModalState.lastTrigger = trigger;
  shVideoModalState.onKeydown = function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') shCloseVideoModal();
  };
  document.addEventListener('keydown', shVideoModalState.onKeydown, false);

  closeBtn.focus();
}

document.addEventListener('click', function (event) {
  var trigger = event.target.closest('[data-sh-video-facade]');
  if (!trigger) return;

  var videoId = trigger.getAttribute('data-youtube-id');
  if (!videoId) return;

  shOpenVideoModal(videoId, trigger.getAttribute('aria-label'), trigger);
}, false);

// ── 2. Hero image gallery — horizontal main-image carousel + thumbnails ──
// The main photo area (.sh-hero__mainimg, marked [data-sh-maintrack]) is a
// real scroll-snap carousel holding every gallery image as a
// [data-sh-slide] — swipeable/draggable directly on touch and trackpads,
// not just a single <img> whose src got swapped. Thumbnail click scrolls
// the carousel to that slide; swiping the carousel directly keeps the
// matching thumbnail highlighted via IntersectionObserver, below. Arrow
// buttons still just scroll the thumbnail strip, unchanged from before.
(function () {
  function setActiveThumb(card, index) {
    var thumbs = card.querySelectorAll('[data-sh-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.toggle('is-active', i === index);
    }
  }

  // Thumbnail click → scroll the main carousel to that slide.
  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-sh-thumb]');
    if (!thumb) return;
    var card = thumb.closest('.sh-hero__img');
    if (!card) return;

    var thumbs = card.querySelectorAll('[data-sh-thumb]');
    var index = Array.prototype.indexOf.call(thumbs, thumb);
    var slides = card.querySelectorAll('[data-sh-slide]');
    var target = slides[index];
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }

    setActiveThumb(card, index);
  }, false);

  // Arrow click → scroll the thumbnail strip (unrelated to the main
  // carousel — this only ever moves the thumbnail row itself).
  document.addEventListener('click', function (event) {
    var prevBtn = event.target.closest('[data-sh-thumb-prev]');
    var nextBtn = event.target.closest('[data-sh-thumb-next]');
    if (!prevBtn && !nextBtn) return;
    var thumbsWrap = (prevBtn || nextBtn).closest('.sh-hero__thumbs');
    var track = thumbsWrap && thumbsWrap.querySelector('.sh-hero__thumbtrack');
    if (track) {
      var delta = prevBtn ? -160 : 160;
      track.scrollBy({ left: delta, top: delta, behavior: 'smooth' });
    }
  }, false);

  // Swiping/dragging the main carousel directly also updates the active
  // thumbnail — watch which slide is most visible with an
  // IntersectionObserver scoped to each track's own scroll container, so
  // this never fights the thumbnail-click handler above (both just set
  // the same class, idempotently).
  if ('IntersectionObserver' in window) {
    var observedTracks = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
    var observedList = observedTracks ? null : [];

    function alreadyObserved(track) {
      if (observedTracks) return observedTracks.has(track);
      return observedList.indexOf(track) > -1;
    }

    function markObserved(track) {
      if (observedTracks) observedTracks.add(track);
      else observedList.push(track);
    }

    function wireTrack(track) {
      if (alreadyObserved(track)) return;
      markObserved(track);

      var card = track.closest('.sh-hero__img');
      if (!card) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-sh-slide]'));
      if (slides.length < 2) return; // single image — nothing to sync

      var io = new IntersectionObserver(function (entries) {
        var best = null;
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
            best = entry;
          }
        }
        if (!best) return;
        var index = slides.indexOf(best.target);
        if (index > -1) setActiveThumb(card, index);
      }, { root: track, threshold: [0.6] });

      for (var i = 0; i < slides.length; i++) io.observe(slides[i]);
    }

    function wireAllTracks() {
      var tracks = document.querySelectorAll('[data-sh-maintrack]');
      for (var i = 0; i < tracks.length; i++) wireTrack(tracks[i]);
    }

    wireAllTracks();

    // Sections can render after initial load (theme editor re-renders,
    // dynamic sections) — watch for new tracks and wire those up too.
    var mo = new MutationObserver(wireAllTracks);
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();

// ── 3. Add-to-cart — AJAX submit + header cart badge + toast ────────────
(function () {
  var toastTimer = null;

  function updateCartBadges(count) {
    var badges = document.querySelectorAll('[data-sh-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      if (count > 0) {
        badges[i].removeAttribute('hidden');
      } else {
        badges[i].setAttribute('hidden', 'hidden');
      }
    }
  }

  function showCartToast(message) {
    var toasts = document.querySelectorAll('[data-sh-cart-toast]');
    if (!toasts.length) return;
    for (var i = 0; i < toasts.length; i++) {
      var toast = toasts[i];
      var msgEl = toast.querySelector('[data-sh-cart-toast-msg]');
      if (msgEl && message) msgEl.textContent = message;
      toast.classList.add('is-visible');
    }
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      for (var j = 0; j < toasts.length; j++) toasts[j].classList.remove('is-visible');
    }, 3500);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-type="add-to-cart-form"]');
    if (!form) return;

    // If Dawn's own assets/product-form.js is also present and active
    // (it lives on the <product-form> custom element these forms are
    // wrapped in, independently of whichever header is installed), IT
    // fires first — form-level listeners run before this document-level
    // one in the bubble phase — and already calls preventDefault() before
    // doing its own AJAX add. Deferring to it here avoids adding the item
    // to the cart TWICE. The one trade-off: in that case this file's cart
    // badge/toast won't update live (Dawn's own add still succeeds, the
    // page just won't show the count until next navigation) — expected
    // and harmless, not a correctness bug. This only matters if Dawn's
    // product-form.js happens to still be wired up; most installs of this
    // header will hit the `else` branch below every time.
    if (event.defaultPrevented) return;

    // No SHConfig means spacehawk-header.liquid isn't on this page (e.g.
    // this section was tested in isolation) — let the form submit and
    // reload normally rather than silently doing nothing.
    if (!window.SHConfig || !window.SHConfig.cartAddUrl) return;

    event.preventDefault();

    var submitBtn = form.querySelector('[type="submit"]');
    var originalText = submitBtn ? submitBtn.textContent : null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';
    }

    function restoreButton() {
      if (!submitBtn) return;
      submitBtn.disabled = false;
      if (originalText !== null) submitBtn.textContent = originalText;
    }

    fetch(window.SHConfig.cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.status) {
          // Shopify's /cart/add.js returns { status, message, description }
          // on failure (e.g. sold out) rather than throwing.
          showCartToast(data.description || 'Couldn’t add that to your cart — please try again.');
          restoreButton();
          return;
        }
        fetch(window.SHConfig.cartJsonUrl)
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            updateCartBadges(cart.item_count);
            showCartToast('Added to cart');
          })
          .catch(function () {
            showCartToast('Added to cart'); // item added fine; badge refresh failed silently
          })
          .then(restoreButton);
      })
      .catch(function () {
        // Network hiccup — fall back to a real form submit so the add
        // still goes through, just without the AJAX polish.
        restoreButton();
        form.submit();
      });
  }, false);

  // ── Buy Now — add to cart, then go straight to checkout ───────────────
  // A plain button (type="button", not submit) inside the same product
  // form, so it never triggers the AJAX add-to-cart submit handler above.
  // On click: POST the form to the same cart-add endpoint, then redirect
  // to checkout on success. If the add itself fails (e.g. sold out),
  // surface it via the existing cart toast instead of redirecting to a
  // checkout with nothing in it.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-sh-pdp-buynow]');
    if (!btn) return;

    var form = btn.closest('form');
    if (!form) return;

    if (!window.SHConfig || !window.SHConfig.cartAddUrl) {
      // No SHConfig (e.g. section tested in isolation) — nothing sane to
      // redirect to, just let the click no-op rather than break.
      return;
    }

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Redirecting…';

    fetch(window.SHConfig.cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.status) {
          showCartToast(data.description || 'Couldn’t add that to your cart — please try again.');
          btn.disabled = false;
          btn.textContent = originalText;
          return;
        }
        window.location.href = window.SHConfig.checkoutUrl || '/checkout';
      })
      .catch(function () {
        // Network hiccup — fall back to a real (non-AJAX) form submit
        // with return_to=/checkout, so the add still goes through and
        // Shopify itself sends the browser on to checkout afterward.
        var returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        returnTo.value = '/checkout';
        form.appendChild(returnTo);
        form.submit();
      });
  }, false);
})();